import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Reveal from "@/components/base/Reveal";
import HeroCarousel from "./components/HeroCarousel";
import { works as worksData } from "@/mocks/works";
import { news as newsData } from "@/mocks/news";
import { profile } from "@/mocks/profile";
import type { Work, NewsItem } from "@/types/content";
import { formatDate } from "@/utils/format";

const works = worksData as Work[];
const news = newsData as NewsItem[];

const entries = [
  {
    to: "/works",
    label: "影视作品",
    en: "FILMOGRAPHY",
    desc: "按类型与年份筛选，查阅电影、电视剧与短片完整档案。",
    icon: "ri-film-line",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20stack%20of%20film%20reels%20and%20clapperboard%20on%20a%20dark%20surface%2C%20deep%20blue%20gray%20tones%20with%20warm%20orange%20accent%20light%2C%20moody%20studio%20still%20life%2C%20minimalist%20composition%2C%20high%20detail%2C%20no%20text&width=800&height=600&seq=okuda-entry-works&orientation=landscape",
    count: `${works.length}`,
    unit: "部作品",
  },
  {
    to: "/news",
    label: "最新动态",
    en: "LATEST NEWS",
    desc: "新作发布、拍摄花絮、活动出席与访谈资讯实时更新。",
    icon: "ri-newspaper-line",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20close%20up%20of%20a%20newspaper%20and%20camera%20on%20a%20desk%20at%20night%2C%20deep%20blue%20gray%20tones%20with%20warm%20orange%20desk%20lamp%20glow%2C%20moody%20editorial%20still%20life%2C%20minimalist%20composition%2C%20high%20detail%2C%20no%20text&width=800&height=600&seq=okuda-entry-news&orientation=landscape",
    count: `${news.length}`,
    unit: "条动态",
  },
];

export default function Home() {
  const featured = works.filter((w) => w.featured).slice(0, 6);
  const latestNews = news.slice(0, 4);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <SiteHeader overlay />

      <main>
        <HeroCarousel works={works} />

        {/* 快捷入口 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {entries.map((entry) => (
                  <Link
                    key={entry.to}
                    to={entry.to}
                    className="group relative rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="relative w-full h-52 md:h-60 overflow-hidden bg-background-200">
                      <img
                        src={entry.image}
                        alt={`${entry.label}栏目入口`}
                        title={`${entry.label} 奥田咲`}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent"></div>
                    </div>
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <div className="flex items-center gap-3">
                        <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                          <i className={`${entry.icon} text-xl`}></i>
                        </span>
                        <div>
                          <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                            {entry.label}
                          </h3>
                          <span className="text-[10px] tracking-[0.28em] text-white/60">
                            {entry.en}
                          </span>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-white/80 leading-6 max-w-md">{entry.desc}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-sm text-accent-300 font-medium">
                          {entry.count} <span className="text-white/60 font-normal">{entry.unit}</span>
                        </span>
                        <span className="flex items-center gap-1 text-sm text-white/90 group-hover:text-accent-300 transition-colors">
                          进入栏目
                          <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* 精选作品 */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <Reveal>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 md:mb-10">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-accent-700">
                    <span className="w-6 h-0.5 bg-accent-500"></span>
                    FEATURED WORKS
                  </span>
                  <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                    精选作品推荐
                  </h2>
                  <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
                    从早期的短片尝试到近年的长片代表作，这几部作品最能体现奥田咲「安静而有力量」的表演风格。
                  </p>
                </div>
                <Link
                  to="/works"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看全部作品
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {featured.map((work, idx) => (
                <Reveal key={work.id} delay={idx * 60}>
                  <WorkCard work={work} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* 资料速览 */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <Reveal className="lg:col-span-5">
              <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={profile.photo}
                  alt="奥田咲个人资料形象照"
                  title="奥田咲 个人资料"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={120}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-accent-700">
                <span className="w-6 h-0.5 bg-accent-500"></span>
                PROFILE
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                演员 · {profile.name}
              </h2>
              <p className="mt-2 text-sm tracking-[0.2em] text-foreground-500">{profile.romanized}</p>
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-8">
                {profile.bio[0]}
              </p>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-cake-2-line", label: "生日", value: profile.birthday },
                  { icon: "ri-map-pin-line", label: "出生地", value: profile.birthplace },
                  { icon: "ri-ruler-line", label: "身高", value: profile.height },
                  { icon: "ri-star-line", label: "出道作品", value: profile.debut },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200"
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-1 text-sm font-medium text-foreground-900 break-words">
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/profile"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                查看完整个人资料
                <i className="ri-arrow-right-line"></i>
              </Link>
            </Reveal>
          </div>
        </section>

        {/* 最新动态 */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto">
            <Reveal>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 md:mb-10">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-accent-700">
                    <span className="w-6 h-0.5 bg-accent-500"></span>
                    LATEST NEWS
                  </span>
                  <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                    最新动态
                  </h2>
                </div>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看更多动态
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {latestNews.map((item, idx) => (
                <Reveal key={item.id} delay={idx * 70}>
                  <Link
                    to={`/news/${item.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer h-full"
                  >
                    <div className="w-full sm:w-40 shrink-0 aspect-[16/10] sm:aspect-[16/11] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} - 奥田咲动态配图`}
                        title={`${item.title} 奥田咲动态`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 font-medium">
                          {item.category}
                        </span>
                        <span className="text-foreground-500">{formatDate(item.date)}</span>
                      </div>
                      <h3 className="mt-2.5 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-6 line-clamp-2">
                        {item.summary}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* 站点简介 */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
          <Reveal className="w-full max-w-7xl mx-auto">
            <div className="rounded-lg bg-primary-900 px-6 md:px-12 py-10 md:py-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <span className="text-xs font-semibold tracking-[0.24em] text-accent-300">
                  ABOUT THIS SITE
                </span>
                <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-white">
                  关于本站
                </h2>
                <p className="mt-4 text-sm md:text-base text-background-300 leading-8">
                  本站为奥田咲影迷与影视内容查询用户的资料整理站点，系统收录其个人资料、影视作品档案与最新动态。内容按类型与年份清晰分类，力求让每一次查阅都快速而舒适。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  了解本站
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/profile"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-primary-700 text-background-100 text-sm font-medium hover:bg-primary-800 transition-colors whitespace-nowrap"
                >
                  个人资料
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}